// Pure heuristic scoring for outdoor activities.
// All inputs SI; temp °C, wind m/s, precip mm/h, AQI on US 1-5 scale (or 0).

export interface ActivityWeather {
  tempC: number;
  feelsLikeC: number;
  humidity: number;
  windMs: number;
  uvi: number;
  aqi?: number; // 1 best — 5 worst
  precipMm?: number; // mm/h or daily total — caller chooses
  cloudPct?: number;
  isDay?: boolean;
}

export type ActivityKey =
  | "running"
  | "cycling"
  | "walking"
  | "golf"
  | "photography";

export interface ActivityScore {
  key: ActivityKey;
  label: string;
  emoji: string;
  score: number; // 0-10
  reason: string;
}

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

// Penalty helpers — return 0..1 multiplier (1=no penalty, 0=block).
function tempBand(tempC: number, ideal: [number, number], soft: number) {
  const [lo, hi] = ideal;
  if (tempC >= lo && tempC <= hi) return 1;
  const dist = tempC < lo ? lo - tempC : tempC - hi;
  return clamp(1 - dist / soft, 0, 1);
}

function precipPenalty(p?: number) {
  if (!p || p < 0.1) return 1;
  if (p < 0.5) return 0.85;
  if (p < 2) return 0.5;
  return 0.15;
}

function windPenalty(w: number, tolerance: number) {
  // tolerance = m/s above which the activity starts to suffer
  if (w <= tolerance) return 1;
  return clamp(1 - (w - tolerance) / 12, 0.05, 1);
}

function aqiPenalty(aqi?: number) {
  if (!aqi || aqi <= 1) return 1;
  if (aqi === 2) return 0.92;
  if (aqi === 3) return 0.7;
  if (aqi === 4) return 0.4;
  return 0.15; // 5 — hazardous
}

function uvPenalty(uvi: number, isDay: boolean | undefined) {
  if (!isDay) return 1;
  if (uvi <= 5) return 1;
  if (uvi <= 7) return 0.92;
  if (uvi <= 10) return 0.78;
  return 0.6;
}

function score(
  base: number,
  ...mults: number[]
): number {
  let s = base;
  for (const m of mults) s *= m;
  return Math.round(clamp(s, 0, 10) * 10) / 10;
}

export function scoreActivities(w: ActivityWeather): ActivityScore[] {
  const t = w.feelsLikeC;
  const wind = w.windMs;
  const aqi = aqiPenalty(w.aqi);
  const uv = uvPenalty(w.uvi, w.isDay);
  const precip = precipPenalty(w.precipMm);

  // Running — ideal 7-17°C feels-like, mild wind tolerance.
  const running = score(
    10 * tempBand(t, [7, 17], 12),
    windPenalty(wind, 7),
    precip,
    aqi,
    uv,
  );

  // Cycling — ideal 12-22°C, low wind very important.
  const cycling = score(
    10 * tempBand(t, [12, 22], 12),
    windPenalty(wind, 5),
    precip * (w.precipMm && w.precipMm > 1 ? 0.6 : 1),
    aqi,
    uv,
  );

  // Walking — wide window, only severe weather hurts.
  const walking = score(
    10 * tempBand(t, [4, 26], 18),
    windPenalty(wind, 9),
    precip,
    aqi * 0.5 + 0.5, // walking less air-sensitive
    uv * 0.6 + 0.4,
  );

  // Golf — needs calm, dry, mild.
  const golf = score(
    10 * tempBand(t, [14, 26], 10),
    windPenalty(wind, 4),
    precip * (w.precipMm && w.precipMm > 0.2 ? 0.5 : 1),
    aqi,
    uv * 0.7 + 0.3,
  );

  // Photography — golden hour bonus is for the time-of-day caller; here we score conditions.
  // Cloud variety helps; dead-clear or fully-overcast are mid; partly cloudy peaks.
  const cloud = w.cloudPct ?? 50;
  const cloudIdeal = 1 - Math.abs(cloud - 50) / 60; // peak at ~50%
  const photography = score(
    10 * tempBand(t, [-5, 30], 25) * clamp(cloudIdeal, 0.4, 1),
    windPenalty(wind, 8),
    precip * (w.precipMm && w.precipMm > 0.5 ? 0.7 : 1),
    aqi * 0.7 + 0.3,
    1,
  );

  const reasonOf = (s: number, hint: string) =>
    s >= 8.5
      ? "Excellent conditions"
      : s >= 7
        ? "Good — go ahead"
        : s >= 5
          ? `Manageable — ${hint}`
          : s >= 3
            ? `Tough — ${hint}`
            : "Skip if you can";

  const wind_warn = wind > 8 ? "windy" : "watch conditions";
  const rain_warn = (w.precipMm ?? 0) > 0.3 ? "wet out" : "borderline";
  const cold_warn = t < 5 ? "cold" : t > 28 ? "hot" : "borderline temp";
  const aqi_warn =
    (w.aqi ?? 0) >= 4
      ? "poor air quality"
      : (w.aqi ?? 0) >= 3
        ? "moderate air"
        : "borderline";

  function pickHint(s: number): string {
    if ((w.precipMm ?? 0) > 0.3) return rain_warn;
    if (wind > 8) return wind_warn;
    if (t < 5 || t > 28) return cold_warn;
    if ((w.aqi ?? 0) >= 3) return aqi_warn;
    return "borderline";
  }

  return [
    {
      key: "running",
      label: "Running",
      emoji: "🏃",
      score: running,
      reason: reasonOf(running, pickHint(running)),
    },
    {
      key: "cycling",
      label: "Cycling",
      emoji: "🚴",
      score: cycling,
      reason: reasonOf(cycling, pickHint(cycling)),
    },
    {
      key: "walking",
      label: "Walking",
      emoji: "🚶",
      score: walking,
      reason: reasonOf(walking, pickHint(walking)),
    },
    {
      key: "golf",
      label: "Golf",
      emoji: "⛳",
      score: golf,
      reason: reasonOf(golf, pickHint(golf)),
    },
    {
      key: "photography",
      label: "Photography",
      emoji: "📷",
      score: photography,
      reason: reasonOf(photography, pickHint(photography)),
    },
  ];
}
